import { useMemo } from 'react';

import { useWallParticipantsStore, type WallBoard, type WallCard } from '@walls/store';

export function useCardPermissions(board: WallBoard, card: WallCard) {
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);

  return useMemo(() => {
    return getCardPermissions(board, card, currentParticipantId);
  }, [board, card, currentParticipantId]);
}

export function getCardPermissions(
  board: WallBoard,
  card: WallCard,
  currentParticipantId: string | null
) {
  const hasParticipant = Boolean(currentParticipantId);
  const hasWorkspaceAccess = Boolean(
    board.isOwner || (hasParticipant && (board.isMember || board.canCollaborate))
  );
  const canSelectBoardAccess = Boolean(board.isOwner || board.isMember || board.canCollaborate);
  const canModifyBoardAccess = Boolean(board.isOwner || board.canCollaborate);
  const ownsCard = Boolean(currentParticipantId && card.ownerId === currentParticipantId);
  const isCluster = isClusterCard(card);
  const canEdit = Boolean(board.isOwner || !board.canOnlyEditOwn || ownsCard);
  const canSelect =
    hasWorkspaceAccess &&
    canSelectBoardAccess &&
    canEdit &&
    (board.allowCardUnlocking || !card.isLocked);
  const canModify = hasWorkspaceAccess && canModifyBoardAccess && canEdit && !card.isLocked;
  const canMoveCluster = isCluster && hasWorkspaceAccess && canSelectBoardAccess && !card.isLocked;
  const canManageClusterLock = ownsCard || Boolean(board.isOwner && !card.ownerId);
  const canLockCluster =
    isCluster &&
    board.allowCardLocking &&
    hasWorkspaceAccess &&
    canSelectBoardAccess &&
    canManageClusterLock &&
    !card.isLocked;
  const canUnlockCluster =
    isCluster &&
    board.allowCardUnlocking &&
    hasWorkspaceAccess &&
    canSelectBoardAccess &&
    canManageClusterLock &&
    card.isLocked;
  const canSortClusterChildren = hasWorkspaceAccess && canSelectBoardAccess;

  return {
    canCluster: board.allowClustering && canModify && !card.parentId && !isFixedShape(card),
    canEdit,
    canEditContent: canModify,
    canInteract: canSelect,
    canLock: isCluster ? canLockCluster : board.allowCardLocking && canModify && !card.isLocked,
    canMove: isCluster ? canMoveCluster : canModify,
    canResize: board.allowCardResizing && canModify && !card.parentId,
    canSelect,
    canSortClusterChildren,
    canUnlock: isCluster
      ? canUnlockCluster
      : board.allowCardUnlocking &&
        hasWorkspaceAccess &&
        canSelectBoardAccess &&
        canEdit &&
        card.isLocked,
    lockedForUser: card.isLocked && !board.allowCardUnlocking,
    showOwnerName: Boolean(
      (board.canOnlyEditOwn || (board.showCardOwnerNames ?? true)) && card.ownerId && !card.isLocked
    ),
  };
}

function isClusterCard(card: WallCard) {
  return Boolean(card.persistCluster || card.cards?.length);
}

function isFixedShape(card: WallCard) {
  const shape = card.shape?.trim().toLowerCase();

  return shape === 'circle' || shape === 'triangle';
}
